<script lang="ts" setup>
import { ref, type PropType, computed, watchEffect } from 'vue';
import { CaseListElement, getContainer } from '@dandelion/core';
import { CaseListServiceInterface } from '@dandelion/core';
import type { CaseServiceInterface } from '@dandelion/core';
import type { CurrentCaseServiceInterface } from '@dandelion/core';
import type TranslationService from '@dandelion/core/src/services/TranslationService';
import { Contexts } from '@dandelion/core';
import { NamedBindings } from '@dandelion/core';
import { type MapForMotivations, Motivations } from '../../utils/MapForMotivations';
import { TypesOfActionHooks } from '@dandelion/core';
import Helpers from '../../utils/Helpers';
import { ReadmissionStorageInterface } from '../../interfaces/ReadmissionStorageInterface';
import ReadmissionHashChange from '../../entities/ReadmissionHashChange';

const props = defineProps({
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  deletingCaseID: {
    type: String as PropType<string>,
    required: true,
  },
  rowElement: {
    type: CaseListElement as PropType<CaseListElement>,
    required: true,
  },
  permaDeletionDialogVisibility: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: false,
  },
});

const container = getContainer(props.context);
const ts = container.get(NamedBindings.TranslationService) as TranslationService;
const caseListService = container.get(NamedBindings.CaseListService) as CaseListServiceInterface;
const mapOfMotivations = container.get(NamedBindings.MapForMotivations) as MapForMotivations;
const caseService = container.get(NamedBindings.CaseService) as CaseServiceInterface;
const currentCaseService = container.get(NamedBindings.CurrentCaseService) as CurrentCaseServiceInterface;

const permaDeleteDialogRef = ref<HTMLDialogElement | null>(null);
const selectedRadioInput = ref('');
const textArea = ref('');
const showTextArea = computed(() => selectedRadioInput.value === Motivations.OTHER_MOTIVATIONS);
const buttonDisabled = computed(
  () =>
    !selectedRadioInput.value || (textArea.value === '' && selectedRadioInput.value === Motivations.OTHER_MOTIVATIONS)
);
const emit = defineEmits(['close-perma-deletion']);

watchEffect(() => {
  if (permaDeleteDialogRef.value) {
    props.permaDeletionDialogVisibility ? permaDeleteDialogRef.value.showModal() : permaDeleteDialogRef.value.close();
  }
});

async function permanentlyDeleteCase(caseID: string, motivation: Motivations | string) {
  await Helpers.runHook(props.context, TypesOfActionHooks.BeforePermanentlyDeleting, caseID, motivation);
  const deletionDate = new Date();
  if (currentCaseService.getCurrentCase().value == caseID) {
    currentCaseService.terminateEditing();
  }
  await caseService.permanentlyDeleteCase(caseID, motivation);
  await caseListService.permanentlyDeleteCase(caseID, motivation, deletionDate);
  emit('close-perma-deletion');
}
</script>

<template>
  <dialog ref="permaDeleteDialogRef" class="perma-deletion-dialog dialog">
    <h3 class="main-title" v-html="`${ts.t('Why are you deleting permanently case')}: ${deletingCaseID.substring(
          0,
          15
    )+ ' - '+ props.rowElement?.cache?.get('core.personalData.name') + ' ' + props.rowElement?.cache?.get('core.personalData.surname') }`" />
    <ul class="list-of-motivations">
      <li v-for="[key, value] in mapOfMotivations.get()" :key="key" class="motivation-item">
        <label class="motivation-label">
          <input
            type="radio"
            name="motivation-input"
            :value="key"
            :class="[value.toLocaleLowerCase().replace(/\s+/g, '-'), 'motivation-input']"
            v-model="selectedRadioInput"
          />
          <span v-html="ts.t(value)" />
        </label>
      </li>
    </ul>
    <textarea
      class="other-motivations-textarea"
      v-show="showTextArea"
      v-model="textArea"
      :placeholder="ts.t('Insert motivations')"
    />
    <section class="buttons-section">
      <button
        type="button"
        class="perma-delete-case-btn primary"
        :disabled="buttonDisabled"
        @click="
          permanentlyDeleteCase(deletingCaseID!, textArea !== '' && textArea !== null ? textArea : selectedRadioInput)
        "
        v-html="ts.t('Confirm permanently delete case')"
      />
      <button
        type="button"
        class="cancel-perma-deletion secondary"
        @click="emit('close-perma-deletion')"
        v-html="ts.t('Cancel')"
      />
    </section>
  </dialog>
</template>

<style>
.perma-deletion-dialog .list-of-motivations {
  padding: 0;
  list-style-type: none;
}

.perma-deletion-dialog .buttons-section {
  display: flex;
  gap: 0.5rem;
}
</style>
